import { useState, useMemo, useCallback } from 'react';
import { useLocale } from '@platform/core';
import { useBudget } from '../contexts/BudgetContext';
import { BudgetEntry } from '../types/budget.types';
import { CsvControls } from '../components/CsvControls';

// Format number without currency symbol - one decimal place
const formatNumber = (value: number): string => {
  return value.toLocaleString('he-IL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

interface EditableCell {
  entryId: string;
  field: 'amount';
  value: number;
}

export const DataEntryPage: React.FC = () => {
  const { t } = useLocale();
  const {
    currentBudget,
    entries,
    updateEntry,
    createEntry,
    deleteEntry,
    getPeriodsList,
    selectedPeriod,
    setSelectedPeriod,
    isLoading,
  } = useBudget();

  const [editingCell, setEditingCell] = useState<EditableCell | null>(null);
  const [filterSubject, setFilterSubject] = useState<string>('');
  const [filterSection, setFilterSection] = useState<string>('');
  const [filterMeasure, setFilterMeasure] = useState<string>('');

  // Filter out period "0" which can be created during initialization
  const periods = getPeriodsList().filter(p => p !== '0');

  // Group entries by period, subject, section for grid display
  const gridData = useMemo(() => {
    if (!currentBudget) return [];

    // Get unique combinations of subject/section
    const combinations = new Map<string, { subject: string; section: string; entries: Map<string, Map<string, BudgetEntry>> }>();

    entries.forEach(entry => {
      const entryAny = entry as any;
      const key = `${entry.subjectId || entryAny.subject}-${entry.sectionId || entryAny.section}`;
      if (!combinations.has(key)) {
        combinations.set(key, {
          subject: (entry as any).subject || entry.subjectId || '',
          section: (entry as any).section || entry.sectionId || '',
          entries: new Map(),
        });
      }

      const combo = combinations.get(key)!;
      if (!combo.entries.has(entry.period)) {
        combo.entries.set(entry.period, new Map());
      }
      combo.entries.get(entry.period)!.set(entry.measure, entry);
    });

    // Convert to array and apply filters
    return Array.from(combinations.values())
      .filter(row => {
        if (filterSubject && !row.subject.toLowerCase().includes(filterSubject.toLowerCase())) return false;
        if (filterSection && !row.section.toLowerCase().includes(filterSection.toLowerCase())) return false;
        return true;
      })
      .map(row => ({
        ...row,
        key: `${row.subject}-${row.section}`,
      }));
  }, [entries, currentBudget, filterSubject, filterSection]);

  const measures = useMemo(() => {
    if (!currentBudget?.measures) return ['budget', 'actual'];
    return currentBudget.measures;
  }, [currentBudget]);

  const filteredMeasures = useMemo(() => {
    if (!filterMeasure) return measures;
    return measures.filter(m => m.toLowerCase().includes(filterMeasure.toLowerCase()));
  }, [measures, filterMeasure]);

  const displayPeriods = useMemo(() => {
    if (selectedPeriod) return [selectedPeriod];
    // Show all periods - use dropdown to focus on specific period
    return periods;
  }, [periods, selectedPeriod]);

  // Format period label based on interval type
  const formatPeriodLabel = (period: string): string => {
    const isWeekly = currentBudget?.intervalType === 'weekly';
    return isWeekly ? `W${period}` : `M${period}`;
  };

  const handleCellClick = useCallback((entry: BudgetEntry | undefined, period: string, measure: string, subject: string, section: string) => {
    if (entry) {
      setEditingCell({
        entryId: entry.id,
        field: 'amount',
        value: entry.amount,
      });
    } else {
      // Create new entry
      setEditingCell({
        entryId: `new-${period}-${measure}-${subject}-${section}`,
        field: 'amount',
        value: 0,
      });
    }
  }, []);

  const handleCellChange = useCallback((value: string) => {
    if (!editingCell) return;
    setEditingCell({
      ...editingCell,
      value: parseFloat(value) || 0,
    });
  }, [editingCell]);

  const handleCellBlur = useCallback(async () => {
    if (!editingCell || !currentBudget) return;

    if (editingCell.entryId.startsWith('new-')) {
      // Parse the new entry ID to get period, measure, subject, section
      const parts = editingCell.entryId.split('-');
      const period = parts[1];
      const measure = parts[2];
      const subject = parts.slice(3, -1).join('-');
      const section = parts[parts.length - 1];

      if (editingCell.value !== 0) {
        await createEntry({
          budgetId: currentBudget.id,
          subjectId: subject,
          sectionId: section,
          period,
          measure: measure as any,
          amount: editingCell.value,
        });
      }
    } else {
      // Update existing entry
      await updateEntry(editingCell.entryId, { amount: editingCell.value });
    }

    setEditingCell(null);
  }, [editingCell, currentBudget, createEntry, updateEntry]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  }, [handleCellBlur]);

  const getEntryValue = useCallback((row: any, period: string, measure: string): BudgetEntry | undefined => {
    return row.entries.get(period)?.get(measure);
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  if (!currentBudget) {
    return (
      <div className="empty-state">
        <h2>{t('budget.noBudgets')}</h2>
        <p>{t('budget.createFirst')}</p>
      </div>
    );
  }

  // Format date for display
  const formatDate = (date: string | Date | undefined): string => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('he-IL');
  };

  return (
    <div className="data-entry-page">
      <div className="page-header">
        <h1>{t('budget.title')} - {t('planning.subjects')}</h1>
        <div className="header-controls">
          <CsvControls className="csv-controls-inline" />
          <select
            className="period-selector"
            value={selectedPeriod || ''}
            onChange={(e) => setSelectedPeriod(e.target.value || null)}
          >
            <option value="">{t('budget.allPeriods')}</option>
            {periods.map(period => (
              <option key={period} value={period}>{period}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="period-info">
        <span className="info-item">
          <strong>{currentBudget.intervalType === 'weekly' ? t('settings.weekly') : t('settings.monthly')}</strong>
        </span>
        <span className="info-item">
          {formatDate(currentBudget.startDate)} - {formatDate(currentBudget.endDate)}
        </span>
        <span className="info-item">
          {periods.length} {currentBudget.intervalType === 'weekly' ? 'שבועות' : 'חודשים'}
        </span>
        {selectedPeriod && (
          <span className="info-item selected-period">
            {t('budget.period')}: {formatPeriodLabel(selectedPeriod)}
          </span>
        )}
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder={t('budget.subject') + '...'}
          value={filterSubject}
          onChange={(e) => setFilterSubject(e.target.value)}
          className="filter-input"
        />
        <input
          type="text"
          placeholder={t('budget.section') + '...'}
          value={filterSection}
          onChange={(e) => setFilterSection(e.target.value)}
          className="filter-input"
        />
        <input
          type="text"
          placeholder={t('budget.measure') + '...'}
          value={filterMeasure}
          onChange={(e) => setFilterMeasure(e.target.value)}
          className="filter-input"
        />
      </div>

      <div className="grid-container">
        <table className="data-grid">
          <thead>
            <tr>
              <th className="sticky-col col-subject">{t('budget.subject')}</th>
              <th className="sticky-col col-section">{t('budget.section')}</th>
              {displayPeriods.map(period => (
                filteredMeasures.map(measure => (
                  <th key={`${period}-${measure}`} className={`col-value ${currentBudget?.intervalType === 'weekly' ? 'col-weekly' : ''}`}>
                    <div className="period-header">{formatPeriodLabel(period)}</div>
                    <div className="measure-header">{measure}</div>
                  </th>
                ))
              ))}
            </tr>
          </thead>
          <tbody>
            {gridData.map(row => (
              <tr key={row.key}>
                <td className="sticky-col col-subject">{row.subject}</td>
                <td className="sticky-col col-section">{row.section}</td>
                {displayPeriods.map(period => (
                  filteredMeasures.map(measure => {
                    const entry = getEntryValue(row, period, measure);
                    const cellKey = `${row.key}-${period}-${measure}`;
                    const isEditing = editingCell?.entryId === (entry?.id || `new-${period}-${measure}-${row.subject}-${row.section}`);

                    return (
                      <td
                        key={cellKey}
                        className={`col-value ${isEditing ? 'editing' : ''} ${currentBudget?.intervalType === 'weekly' ? 'col-weekly' : ''}`}
                        onClick={() => handleCellClick(entry, period, measure, row.subject, row.section)}
                      >
                        {isEditing ? (
                          <input
                            type="number"
                            value={editingCell?.value || 0}
                            onChange={(e) => handleCellChange(e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            className="cell-input"
                          />
                        ) : (
                          <span className={`cell-value ${entry?.amount ? '' : 'empty'}`}>
                            {entry?.amount ? formatNumber(entry.amount) : '-'}
                          </span>
                        )}
                      </td>
                    );
                  })
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid-info">
        {t('common.noData') !== 'noData' ? `${gridData.length} ${t('common.actions')}` : `${gridData.length} rows`}
      </div>

      <style>{`
        .data-entry-page {
          max-width: 100%;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-header h1 {
          font-size: 1.5rem;
          margin: 0;
        }

        .header-controls {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .csv-controls-inline {
          padding: 0;
          border: none;
          background: none;
        }

        .period-selector {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
        }

        .period-info {
          display: flex;
          gap: 1.5rem;
          margin-bottom: 1rem;
          padding: 0.75rem 1rem;
          background: var(--color-background);
          border-radius: var(--radius);
          font-size: 0.875rem;
          flex-wrap: wrap;
        }

        .info-item {
          color: var(--color-text-secondary);
        }

        .info-item strong {
          color: var(--color-text);
        }

        .info-item.selected-period {
          color: var(--color-primary);
          font-weight: 500;
        }

        .filters {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .filter-input {
          padding: 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          min-width: 150px;
        }

        .grid-container {
          overflow-x: auto;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
        }

        .data-grid {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .data-grid th,
        .data-grid td {
          padding: 0.5rem;
          border: 1px solid var(--color-border);
          text-align: center;
        }

        .data-grid th {
          background: var(--color-bg);
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 1;
        }

        .sticky-col {
          position: sticky;
          background: var(--color-surface);
          z-index: 2;
        }

        .col-subject {
          left: 0;
          min-width: 120px;
          text-align: start;
          font-weight: 500;
        }

        .col-section {
          left: 120px;
          min-width: 100px;
          text-align: start;
        }

        .col-value {
          min-width: 100px;
          cursor: pointer;
        }

        .col-value.col-weekly {
          min-width: 70px;
          padding: 0.25rem;
        }

        .col-value:hover {
          background: var(--color-bg);
        }

        .col-value.editing {
          padding: 0;
          background: #fff;
        }

        .period-header {
          font-weight: 600;
          font-size: 0.8rem;
        }

        .col-weekly .period-header {
          font-size: 0.7rem;
        }

        .measure-header {
          font-size: 0.7rem;
          color: var(--color-text-secondary);
        }

        .col-weekly .measure-header {
          font-size: 0.6rem;
        }

        .cell-input {
          width: 100%;
          padding: 0.5rem;
          border: 2px solid var(--color-primary);
          border-radius: 0;
          text-align: center;
          font-size: 0.875rem;
        }

        .cell-value {
          display: block;
        }

        .cell-value.empty {
          color: var(--color-text-secondary);
        }

        .grid-info {
          padding: 0.5rem;
          text-align: end;
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }
      `}</style>
    </div>
  );
};
